/**
 * Display helpers for the personality (IPIP-50) trait profile stored on an attempt as
 * `personalityResult` (see server/modules/assessment/personality/ipip50.js).
 *
 * IPIP publishes no norms and warns against sorting people into categories, so we show the
 * raw score on its full 10-50 range — never Low/Average/High bands and never a %.
 */

export const PERSONALITY_KIND = 'personality-ipip50';

export const PERSONALITY_TRAITS = [
  { key: 'extraversion', short: 'E', label: 'Extraversion', low: 'Reserved, quiet', high: 'Outgoing, talkative' },
  { key: 'agreeableness', short: 'A', label: 'Agreeableness', low: 'Direct, sceptical', high: 'Warm, cooperative' },
  { key: 'conscientiousness', short: 'C', label: 'Conscientiousness', low: 'Flexible, spontaneous', high: 'Organised, dependable' },
  { key: 'emotionalStability', short: 'S', label: 'Emotional Stability', low: 'Feels stress more strongly', high: 'Calm, even-tempered' },
  { key: 'intellect', short: 'I', label: 'Intellect / Imagination', low: 'Practical, concrete', high: 'Curious, imaginative' },
];

export const LIKERT_LABELS = {
  1: 'Very Inaccurate',
  2: 'Moderately Inaccurate',
  3: 'Neither Accurate Nor Inaccurate',
  4: 'Moderately Accurate',
  5: 'Very Accurate',
};

export const QUALITY_FLAG_LABELS = {
  same_answer_overuse: 'Used one answer for half or more of the statements',
  inconsistent_answers: 'Gave contradictory answers to opposite statements',
  too_fast: 'Finished in under two minutes',
  unanswered_items: 'Left some statements unanswered',
};

export const PERSONALITY_CREDIT =
  'Based on the IPIP Big-Five Factor Markers (Goldberg, 1992), a public-domain research instrument. Advisory only — not a pass/fail measure.';

export function isPersonalityResult(result) {
  return Boolean(result && Array.isArray(result.traits) && result.traits.length > 0);
}

export function traitScore(result, key) {
  const t = (result?.traits || []).find((x) => x.trait === key);
  return t && typeof t.raw === 'number' ? t.raw : null;
}

/** Compact table text, e.g. "E 38 · A 41 · C 44 · S 29 · I 35" ("–" for an incomplete trait). */
export function formatPersonalityChip(result) {
  if (!isPersonalityResult(result)) return '';
  return PERSONALITY_TRAITS.map((t) => {
    const raw = traitScore(result, t.key);
    return `${t.short} ${raw === null ? '–' : Math.round(raw)}`;
  }).join(' · ');
}

/** A screening row's profile: standalone (`personalityResult`) or from a personality module. */
export function personalityResultForRow(row) {
  if (isPersonalityResult(row?.personalityResult)) return row.personalityResult;
  const modules = Array.isArray(row?.assessmentModuleScores) ? row.assessmentModuleScores : [];
  const withProfile = modules.find((m) => isPersonalityResult(m?.personalityResult));
  return withProfile ? withProfile.personalityResult : null;
}

/**
 * Excel columns for the trait profile — one per trait plus a confidence column — or [] when
 * no row in the export has a profile.
 */
export function personalityExportColumns(rows) {
  if (!(rows || []).some((r) => personalityResultForRow(r))) return [];
  const traitCols = PERSONALITY_TRAITS.map((t) => ({
    header: `${t.label} (10-50)`,
    get: (row) => {
      const raw = traitScore(personalityResultForRow(row), t.key);
      return raw === null ? 'N/A' : Math.round(raw);
    },
  }));
  return [
    ...traitCols,
    {
      header: 'Work Style Confidence',
      get: (row) => {
        const result = personalityResultForRow(row);
        if (!result) return 'N/A';
        return result.quality?.lowConfidence ? 'Low' : 'OK';
      },
    },
  ];
}

/** "Extraversion 38/50, ..." for tooltips and exports. */
export function describePersonality(result) {
  if (!isPersonalityResult(result)) return '';
  return PERSONALITY_TRAITS.map((t) => {
    const raw = traitScore(result, t.key);
    return `${t.label} ${raw === null ? 'incomplete' : `${Math.round(raw)}/50`}`;
  }).join(', ');
}
